import {Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {StartupService} from './startup.service';
import {modernDialogConfig} from './dialog-config';
import {CreateProjectComponent} from '../component/projects/project/dialogs/create-project/create-project.component';

/**
 * What {@link CreateProjectComponent} closes with. `created: false`
 * (or a null result) means the operator cancelled — nothing was
 * written.
 *
 * `project_ID` / `primaryDraft_ID` come from the consolidated
 * `POST /project/createproject` envelope. They can legitimately be
 * 0 when the server responded without `data` (older builds), which
 * is why callers that want to select the new Project fall back to
 * matching on `projectName`.
 */
export interface CreateProjectDialogResult {
  created: boolean;
  projectName?: string;
  project_ID?: number;
  primaryDraft_ID?: number;
  /** The operator left "start a Draft" ticked, so open the Draft editor. */
  openDraft?: boolean;
}

/**
 * Single entry point for "Create Project" from anywhere in the app.
 *
 * Before this existed the flow was hard-wired inside
 * `ProjectsComponent.createProject(...)`, so every other surface
 * that wanted to send an operator to create their first Project had
 * to either duplicate the dialog wiring or — as the empty cart page
 * did — link at a route that does not exist (`/secure/books`, which
 * has never been registered in `app-routing.module.ts`, so the
 * button silently did nothing).
 *
 * Components already on the Projects screen call {@link open} and
 * handle the result themselves. Components elsewhere call
 * {@link createProjectAndOpenDraft}, which hands off to
 * `/secure/projects` with query params describing what to select;
 * `ProjectsComponent` consumes those on load.
 */
@Injectable({providedIn: 'root'})
export class CreateProjectLauncherService {

  constructor(private dialog: MatDialog, private router: Router, private startupService: StartupService) {
  }

  /**
   * Open the Create Project dialog and emit its result once.
   *
   * The dialog needs the current `Organization` for its folder
   * tree; resolving that here (rather than requiring every caller
   * to pass one) is what lets surfaces like the cart page open it
   * without knowing anything about Projects.
   */
  open(selectedFolder: string = '/'): Observable<CreateProjectDialogResult> {
    // `modernDialogConfig`, not `new MatDialogConfig()` — see that
    // file's header: a constructed config silently drops the app-wide
    // `modern-dialog` panelClass and with it the whole dialog skin.
    const dialogConfig = modernDialogConfig({ width: '520px' });
    dialogConfig.data = {
      organization: this.startupService.getCurrentOrganization(),
      selectedFolder: selectedFolder
    };
    const dialogRef = this.dialog.open(CreateProjectComponent, dialogConfig);
    return dialogRef.afterClosed().pipe(map(result => {
      if ( !result || !result.created ) {
        return { created: false } as CreateProjectDialogResult;
      }
      return result as CreateProjectDialogResult;
    }));
  }

  /**
   * Open the dialog and, on success, navigate to the Projects
   * screen with enough context for it to select the new Project and
   * (when the operator asked for one) open its Draft for editing.
   *
   * The ids travel as query params rather than through a shared
   * service field because the navigation is a full route change:
   * `ProjectsComponent` is constructed *after* the dialog closed, so
   * there is nothing listening at the moment the result arrives.
   */
  createProjectAndOpenDraft(selectedFolder: string = '/'): void {
    this.open(selectedFolder).subscribe(result => {
      if ( !result || !result.created ) {
        return;
      }
      const queryParams: any = {};
      if ( result.project_ID ) { queryParams.newProjectId = result.project_ID; }
      if ( result.projectName ) { queryParams.newProjectName = result.projectName; }
      queryParams.openDraft = result.openDraft ? '1' : '0';
      this.router.navigate(['/secure/projects'], { queryParams: queryParams });
    });
  }
}


